import { autoinject, bindable, customElement, observable, BindingEngine, Disposable } from 'aurelia-framework';
import { AlertService } from '@aurelia-toolkit/alert';
import { CustomerService } from '@dynamik/common';
import { AdvancedFilterModel, GetContextMenuItemsParams, GridApi } from 'ag-grid-community';
import { MdcMenu } from '@aurelia-mdc-web/menu';
import { AgGridAurelia } from '../../ag-grid-aurelia';
import { AgGridState } from '../../classes/agGridState';
import { skip, Subject, takeUntil } from '../../rxjs-local';
import { IExcelExportParamsProvider } from '../../resources/agGrid/i-excel-export-params-provider';
import { GridFiltersDialog, IGridFiltersDialogModel } from '../dialogs/grid-filters-dialog/grid-filters-dialog';
import { buildAdvancedFilterModel, describeCondition, getGridFilterColumns, IGridFilterColumn, IGridFilterCondition, IGridFilterState } from './grid-filter-model';

@autoinject
@customElement('grid-filter')
export class GridFilter {
  constructor(private alertService: AlertService, private agGridState: AgGridState, private bindingEngine: BindingEngine,
    private customerService: CustomerService) { }

  private appliedConditions: IGridFilterCondition[] = [];
  private apiSubscription: Disposable;
  private detached$ = new Subject<void>();
  private gridApi: GridApi;

  chips: string[] = [];
  exportMenu: MdcMenu;
  exportAnchor: HTMLElement;

  @bindable
  grid: AgGridAurelia;

  @bindable
  stateKey: string;

  @bindable
  excelExportParams: IExcelExportParamsProvider;

  /** Overrides the column list derived from the grid (dialog dropdown + chip labels). */
  @bindable
  columns: IGridFilterColumn[];

  /**
   * Server-side mode: when bound, the element does not touch the grid's quick filter or advanced filter model.
   * Instead every filter change invokes the callback with { text, model } and the consumer applies it
   * (e.g. injects it into a server-side page request and refreshes).
   */
  @bindable
  changed: (args: { text: string; model: AdvancedFilterModel | null }) => void;

  @observable
  searchText = '';
  searchTextChanged() {
    if (!this.gridApi) {
      return;
    }
    if (this.searchText && this.appliedConditions.length) {
      this.appliedConditions = [];
      this.updateChips();
    }
    this.applyFilter();
  }

  attached() {
    if (this.grid?.api) {
      this.init(this.grid.api);
    } else if (this.grid) {
      // the grid creates its api in a queued task after attach
      this.apiSubscription = this.bindingEngine.propertyObserver(this.grid, 'api')
        .subscribe((api: GridApi) => {
          if (api) {
            this.apiSubscription.dispose();
            this.apiSubscription = undefined;
            this.init(api);
          }
        });
    }
    // the persisted filter is stored per company
    this.customerService.selectedCustomer$.pipe(skip(1), takeUntil(this.detached$)).subscribe(() => {
      if (this.gridApi) {
        this.restore(this.serverMode);
      }
    });
  }

  detached() {
    this.apiSubscription?.dispose();
    this.detached$.next();
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.removeEventListener('filterChanged', this.filterChangedListener);
      this.gridApi.removeEventListener('firstDataRendered', this.firstDataRenderedListener);
    }
  }

  private get serverMode(): boolean {
    return !!this.changed;
  }

  private init(api: GridApi) {
    this.gridApi = api;
    api.addEventListener('filterChanged', this.filterChangedListener);
    if (this.serverMode) {
      // restore right away so the earliest possible server request carries the persisted filter
      this.restore();
    } else {
      api.addEventListener('firstDataRendered', this.firstDataRenderedListener);
    }
  }

  private firstDataRenderedListener = () => {
    this.restore();
  };

  private filterChangedListener = () => {
    if (this.serverMode) {
      return;
    }
    if (!this.gridApi.getAdvancedFilterModel() && !this.searchText && this.appliedConditions.length) {
      this.appliedConditions = [];
      this.updateChips();
      this.persist();
    }
  };

  async openDialog() {
    if (!this.gridApi) {
      return;
    }
    const model: IGridFiltersDialogModel = {
      columns: this.getColumns(),
      conditions: this.appliedConditions.map(x => ({ ...x }))
    };
    if (await this.alertService.open({ viewModel: GridFiltersDialog, model }) !== 'save') {
      return;
    }
    this.appliedConditions = model.conditions;
    this.updateChips();
    if (this.searchText) {
      this.searchText = ''; // triggers searchTextChanged -> applyFilter
    } else {
      this.applyFilter();
    }
  }

  chipClicked(detail: { shouldRemove: boolean }) {
    // the interaction event also fires for the trailing remove action
    if (!detail.shouldRemove) {
      this.openDialog();
    }
  }

  chipRemoved(detail: { chipIndex: number; isComplete: boolean }) {
    // the chip set emits removal twice: at exit-animation start and after DOM removal
    if (!detail.isComplete) {
      this.removeChip(detail.chipIndex);
    }
  }

  removeChip(index: number) {
    this.appliedConditions.splice(index, 1);
    if (this.appliedConditions.length) {
      this.appliedConditions[0].joinWithPrevious = undefined;
    }
    this.updateChips();
    this.applyFilter();
  }

  exportCsv() {
    this.gridApi?.exportDataAsCsv({ columnSeparator: ';' });
  }

  async exportExcel() {
    if (!this.gridApi) {
      return;
    }
    let params;
    if (this.excelExportParams) {
      await this.excelExportParams.loadData?.();
      params = await this.excelExportParams.getExportToExcelParams({ api: this.gridApi } as GetContextMenuItemsParams);
    }
    this.gridApi.exportDataAsExcel(params);
  }

  private applyFilter() {
    const model = buildAdvancedFilterModel(this.appliedConditions, this.getColumns());
    if (this.serverMode) {
      this.changed({ text: this.searchText, model });
    } else {
      this.gridApi.setGridOption('quickFilterText', this.searchText);
      if (model || this.gridApi.getAdvancedFilterModel()) {
        this.gridApi.setAdvancedFilterModel(model);
      }
    }
    this.persist();
  }

  private restore(force = false) {
    const state = this.stateKey ? this.agGridState.restoreGridFilter<IGridFilterState>(this.stateKey) : null;
    const conditions = state?.conditions ?? [];
    const text = conditions.length ? '' : state?.text ?? '';
    if (!force && !conditions.length && !this.appliedConditions.length && text === this.searchText) {
      return;
    }
    this.appliedConditions = conditions;
    this.updateChips();
    if (text !== this.searchText) {
      this.searchText = text; // triggers searchTextChanged -> applyFilter
    } else {
      this.applyFilter();
    }
  }

  private getColumns(): IGridFilterColumn[] {
    if (this.columns) {
      return this.columns;
    }
    return this.gridApi ? getGridFilterColumns(this.gridApi) : [];
  }

  private updateChips() {
    this.chips = this.appliedConditions.map(x => describeCondition(x, this.getColumns()));
  }

  private persist() {
    if (!this.stateKey || !this.gridApi) {
      return;
    }
    const state: IGridFilterState = this.appliedConditions.length
      ? { conditions: this.appliedConditions }
      : { text: this.searchText };
    this.agGridState.saveGridFilter(this.stateKey, state);
  }
}
